import { Link } from "@tanstack/react-router";
import { Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ListingView } from "#/../convex/listings";
import { Disc3D } from "#/components/disc/Disc3D";
import { COLOR_HEX } from "#/lib/constants";
import { cn } from "#/lib/utils";

export type ListingCardData = Pick<
	ListingView,
	| "_id"
	| "discModelId"
	| "imageTopUrl"
	| "colors"
	| "condition"
	| "price"
	| "priceNegotiable"
	| "manufacturerId"
	| "manufacturerName"
	| "discModelName"
	| "discClass"
	| "plasticType"
	| "weight"
	| "officialSpeed"
	| "officialGlide"
	| "officialTurn"
	| "officialFade"
	| "estimatedSpeed"
	| "estimatedGlide"
	| "estimatedTurn"
	| "estimatedFade"
	| "city"
	| "country"
	| "shipsTo"
>;

function getDiscRimColor(colors: string[]): string {
	for (const c of colors) {
		if (COLOR_HEX[c]) return COLOR_HEX[c];
	}
	return "#888888";
}

const CONDITION_STYLE: Record<string, string> = {
	"9": "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-400",
	"8": "bg-sky-500/15 text-sky-700 dark:bg-sky-400/20 dark:text-sky-400",
	"7": "bg-amber-500/15 text-amber-800 dark:bg-amber-400/20 dark:text-amber-400",
	"6": "bg-stone-400/15 text-stone-700 dark:bg-stone-400/20 dark:text-stone-400",
	"5": "bg-stone-400/15 text-stone-700 dark:bg-stone-400/20 dark:text-stone-400",
	"1-3": "bg-rose-500/15 text-rose-700 dark:bg-rose-400/20 dark:text-rose-400",
};

const CARD_DISC_SIZE = 160;
const FLIGHT_KEYS = ["speed", "glide", "turn", "fade"] as const;

export function ListingCard({
	listing,
	isSaved,
	onSaveToggle,
	eager = false,
	priority = false,
}: {
	listing: ListingCardData;
	isSaved?: boolean;
	onSaveToggle?: () => void;
	/** Don't lazy-load the disc image (for above-the-fold cards). */
	eager?: boolean;
	/** Mark as LCP image — sets fetchpriority=high (use only for the first card). */
	priority?: boolean;
}) {
	const { t } = useTranslation("listings");
	const rimColor = getDiscRimColor(listing.colors);
	const hasImage = !!listing.imageTopUrl;

	const flightNums = [
		listing.officialSpeed ?? listing.estimatedSpeed,
		listing.officialGlide ?? listing.estimatedGlide,
		listing.officialTurn ?? listing.estimatedTurn,
		listing.officialFade ?? listing.estimatedFade,
	];
	const hasFlightNums = flightNums.every((n) => n != null);

	const specStrip = [
		listing.plasticType,
		listing.weight ? `${listing.weight}g` : null,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<Link
			to="/listings/$id"
			params={{ id: listing._id }}
			className="group block rounded-[var(--radius)] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
		>
			<article className="overflow-hidden rounded-[var(--radius)] border border-border bg-card transition-all duration-150 group-hover:-translate-y-1 group-hover:shadow-lg">
				{/* Disc image area */}
				<div
					className="relative flex h-[210px] items-center justify-center overflow-hidden"
					style={{
						background: `radial-gradient(ellipse at 50% 70%, ${rimColor}22, transparent 65%), var(--muted)`,
						viewTransitionName: `disc-${listing._id}`,
					}}
				>
					{hasImage ? (
						<Disc3D
							topUrl={listing.imageTopUrl ?? ""}
							bottomUrl={undefined}
							rimColor={rimColor}
							discClass={listing.discClass}
							size={CARD_DISC_SIZE}
							lazy={!eager && !priority}
							priority={priority}
						/>
					) : (
						<DiscPlaceholder rimColor={rimColor} />
					)}

					{/* Condition badge */}
					<span
						className={cn(
							"absolute left-2.5 top-2.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em]",
							CONDITION_STYLE[listing.condition] ??
								"bg-muted text-muted-foreground",
						)}
					>
						{t(`card.condition.${listing.condition}` as "card.condition.9")}
					</span>

					{/* Watchlist toggle */}
					{onSaveToggle && (
						<button
							type="button"
							aria-label={
								isSaved
									? t("card.removeFromWatchlist")
									: t("card.saveToWatchlist")
							}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onSaveToggle();
							}}
							className={cn(
								"absolute right-2.5 top-2.5 flex h-[30px] w-[30px] items-center justify-center rounded-full transition-all duration-150",
								"bg-black/25 backdrop-blur-sm hover:bg-black/45",
								isSaved
									? "opacity-100"
									: "[@media(hover:none)]:opacity-100 opacity-0 group-hover:opacity-100",
							)}
						>
							<Heart
								className={cn(
									"h-3.5 w-3.5 transition-colors",
									isSaved ? "fill-rose-400 text-rose-400" : "text-white",
								)}
							/>
						</button>
					)}

					{/* Spec strip */}
					{specStrip && (
						<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/30 dark:from-black/55 to-transparent px-3.5 pb-2.5 pt-5">
							<p className="text-[11px] font-semibold text-white/90">
								{specStrip}
							</p>
						</div>
					)}
				</div>

				{/* Info */}
				<div className="px-4 pb-4 pt-3.5">
					<p className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
						{listing.manufacturerName ?? "Manufacturer"}
					</p>
					<h2 className="display-title mb-3 text-[26px] leading-none text-foreground line-clamp-1">
						{listing.discModelName ?? "Disc Listing"}
					</h2>

					{/* Flight numbers */}
					<div className="mb-3 grid grid-cols-4 divide-x divide-border overflow-hidden rounded-[var(--radius)] border border-border text-center">
						{FLIGHT_KEYS.map((key, i) => (
							<div key={key} className="bg-muted/60 py-1.5">
								<div className="text-[8px] font-bold uppercase tracking-[0.1em] text-muted-foreground block mb-0.5">
									{t(`flightNumbers.${key}`)}
								</div>
								<div
									className={cn(
										"display-title text-xl leading-none",
										hasFlightNums
											? "text-foreground"
											: "text-muted-foreground/30",
									)}
								>
									{hasFlightNums ? flightNums[i] : "—"}
								</div>
							</div>
						))}
					</div>

					{/* Price + location */}
					<div className="flex items-center justify-between">
						<div className="flex items-baseline gap-1">
							<span className="display-title text-[32px] leading-none text-primary-accessible">
								€{listing.price}
							</span>
							{listing.priceNegotiable && (
								<span className="text-[11px] font-semibold text-muted-foreground">
									{t("card.negotiable")}
								</span>
							)}
						</div>
						<span className="text-[11px] font-medium text-muted-foreground self-end pb-0.5">
							{listing.city}
							{listing.city && listing.shipsTo ? " · " : ""}
							{t(
								`card.shipsTo.${listing.shipsTo}` as "card.shipsTo.domestic",
							) ?? listing.shipsTo}
						</span>
					</div>
				</div>
			</article>
		</Link>
	);
}

function DiscPlaceholder({ rimColor }: { rimColor: string }) {
	return (
		<svg viewBox="0 0 200 200" width="145" height="145" aria-hidden="true">
			<circle
				cx="100"
				cy="100"
				r="90"
				fill="none"
				stroke={rimColor}
				strokeWidth="14"
			/>
			<circle
				cx="100"
				cy="100"
				r="62"
				fill="none"
				stroke={rimColor}
				strokeWidth="2"
				opacity=".4"
			/>
			<circle
				cx="100"
				cy="100"
				r="44"
				fill="none"
				stroke={rimColor}
				strokeWidth="1.5"
				opacity=".22"
			/>
			<circle cx="100" cy="100" r="28" fill={rimColor} opacity=".09" />
			<circle cx="100" cy="100" r="10" fill={rimColor} opacity=".3" />
			<defs>
				<radialGradient id="disc-placeholder-g" cx="35%" cy="35%">
					<stop offset="0%" stopColor="white" stopOpacity=".3" />
					<stop offset="100%" stopColor="white" stopOpacity="0" />
				</radialGradient>
			</defs>
			<circle cx="100" cy="100" r="90" fill="url(#disc-placeholder-g)" />
		</svg>
	);
}
